import axios from 'axios';
import { endpoints } from '../config/endpoints';
import ResponseModel from '../models/response-model';
import SmgToken from '../models/smg-token';
import * as storageHelper from '../helpers/storage-helper';
import { getConfiguredSmgAuthBase } from '../helpers/smg-auth-helper';

// Request config carrying the Teams SSO token for endpoints guarded by [MsTeamsAuthorized]; empty when none is stored.
function teamsSsoConfig() {
  const teamsSsoToken = storageHelper.getMsSsoToken();

  return teamsSsoToken ? { headers: { 'Teams-Sso-Authorization': teamsSsoToken } } : {};
}

function isAuthenticated() {
  const url = `${endpoints.backEndBase}/auth/isAuthenticated`;

  return axios.get<boolean>(url, teamsSsoConfig());
}

// Returns the SMG token pair the backend stored for this Teams user, so the tab can restore its session after the
// Teams desktop client dropped the cookies (TEAM-1883).
function getSmgToken() {
  const url = `${endpoints.backEndBase}/auth/get-smg-token`;

  return axios.get<SmgToken>(url, teamsSsoConfig());
}

function authenticate() {
  const url = `${endpoints.backEndBase}/auth/authenticate`;

  const teamsSsoToken = storageHelper.getMsSsoToken();
  let config = {};

  const refreshToken = storageHelper.getSmgRefreshToken();
  if (teamsSsoToken && refreshToken) {
    config = {
      headers: {
        'Teams-Sso-Authorization': teamsSsoToken,
        'Smg-Refresh-Token': refreshToken,
      },
    };
  }

  return axios.post<boolean>(url, null, config);
}

function updateSmgTokens() {
  const url = `${endpoints.backEndBase}/auth/update-smg-tokens`;

  const refreshToken = storageHelper.getSmgRefreshToken();
  let config = {};

  if (refreshToken) {
    config = {
      headers: {
        'Smg-Refresh-Token': refreshToken,
      },
    };
  }

  return axios.post<boolean>(url, null, config);
}

// A logout that gets no answer within this time is reported as failed; the user stays logged in and can retry.
const LOGOUT_TIMEOUT_MS = 15000;

function logout() {
  const url = `${endpoints.backEndBase}/auth/logout`;

  return axios.post<ResponseModel>(url, null, { ...teamsSsoConfig(), timeout: LOGOUT_TIMEOUT_MS });
}

// REACT_APP_SMG_AUTH_BASE is the source of truth. The smgAuthBase URL parameter is only a fallback, and
// storageHelper.getAuthBase() returns it only for trusted Dstny domains (TEAM-2166).
function getSmgAuthBase(): string | null {
  const configuredAuthBase = getConfiguredSmgAuthBase();
  if (configuredAuthBase) {
    return configuredAuthBase;
  }

  console.warn('REACT_APP_SMG_AUTH_BASE is not configured, falling back to the smgAuthBase URL parameter');
  return storageHelper.getAuthBase();
}

function getSmgLoginUrl(action: string): string | null {
  const authBase = getSmgAuthBase();
  if (!authBase) {
    return null;
  }

  const realm = encodeURIComponent(storageHelper.getRealm());
  const client = encodeURIComponent(storageHelper.getClient());
  return `${authBase}/login/${action}/realm/${realm}/client/${client}`;
}

const smgAuthBaseNotConfiguredErrorName = 'SmgAuthBaseNotConfiguredError';

function missingSmgAuthBase() {
  const error = new Error('SMG auth base is not configured');
  error.name = smgAuthBaseNotConfiguredErrorName;
  return Promise.reject(error);
}

// Lets callers tell a missing/untrusted SMG auth base (a configuration problem) apart from network errors.
function isSmgAuthBaseNotConfiguredError(error: unknown): boolean {
  return error instanceof Error && error.name === smgAuthBaseNotConfiguredErrorName;
}

function getLoginUrl() {
  const url = getSmgLoginUrl('getloginurl');
  if (!url) {
    return missingSmgAuthBase();
  }
  return axios.post<{ loginUrl: string }>(url);
}

function getAccessToken(code: string) {
  const url = getSmgLoginUrl('getaccesstoken');
  if (!url) {
    return missingSmgAuthBase();
  }
  const body = {
    code,
    realm: storageHelper.getRealm(),
  };
  return axios.post<SmgToken>(url, body);
}

function refreshToken(accessToken: string, refreshToken: string) {
  const url = getSmgLoginUrl('refreshtoken');
  if (!url) {
    return missingSmgAuthBase();
  }
  const body = {
    refreshToken,
    jwt: accessToken,
  };
  return axios.post<SmgToken>(url, body);
}

function logoutFromSmg(refreshToken: string) {
  if (!refreshToken) {
    return;
  }

  const url = getSmgLoginUrl('logout');
  if (!url) {
    return;
  }

  const body = {
    refreshToken,
  };

  return axios.post(url, body);
}

export {
  isAuthenticated,
  getSmgToken,
  authenticate,
  updateSmgTokens,
  logout,
  getLoginUrl,
  getAccessToken,
  refreshToken,
  logoutFromSmg,
  isSmgAuthBaseNotConfiguredError,
};
